import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  getCraftsmanAdjustment,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateWorkshopAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentificationEntities: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
) => {
  // No unit-rate adjustments for workshop
  if (salesComparisonApproach === 'unit_rate') return undefined;

  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentificationEntities.subject;
  const compPropertyIdentification = propertyIdentificationEntities[compID];

  if (
    !subjectImprovements ||
    !compImprovements ||
    !depreciationRatios ||
    !subjectPropertyIdentification ||
    !compPropertyIdentification
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { workshop } = subjectImprovements;
  const { workshop: compWorkshop } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) return undefined;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);
  return adjCalculator.getWorkshopAdj({
    subject: {
      hasFeature: ORDER_FEATURES.workshop.hasFeature(subjectImprovements),
      sqft: workshop?.area ? AloftArea.schema.parse(workshop?.area) : undefined,
      quality: workshop?.quality,
      zipCode,
    },
    comp: {
      hasFeature: ORDER_FEATURES.workshop.hasFeature(compImprovements),
      sqft: compWorkshop?.area ? AloftArea.schema.parse(compWorkshop?.area) : undefined,
      quality: compWorkshop?.quality,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectWorkshopAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'workshop'),
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
  ],
  calculateWorkshopAdj,
);

export const selectWorkshopAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'workshop'),
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
  ],
  (
    adjustmentVersion,
    improvements,
    propertyIdentification,
    depreciationRatios,
    compSequence,
    compTypes,
    salesComparisonApproach,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateWorkshopAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
        compTypes,
        salesComparisonApproach,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedWorkshopAdj = createPriceSelector(
  [selectWorkshopAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'workshop', adjSettings),
);

export const selectAppliedWorkshopAdjDisplay = createSelector([selectAppliedWorkshopAdj], (adj) => {
  if (!adj) return undefined;

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedWorkshopAdjsDisplay = createSelector(
  [selectWorkshopAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = adjs[propID];
      const manualAdjustment = manualAdjustmentEntities[propID]?.workshop;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adjs[propID], 'workshop', adjSettings),
          ),
          color: getColorFromAdjustment(adjs[propID]),
        };
      }

      return acc;
    }, {}),
);
